/**
 * "You're on a shared device" — who is at the keyboard, before anything else.
 *
 * A shop-floor PC is signed in as the device, so without this every ticket
 * would come from "Press Line 3" and IT would have nobody to call back. The
 * name is required (2–80 characters); how to reach them is optional, because
 * plenty of operators have no phone and their supervisor is the honest answer
 * (1 Oct). Both live in sessionStorage only (`@/lib/kiosk-person`).
 */

import { Icon, Monitor } from "@norican/icons";
import { Button, Card, Field, Form, Input, Typography } from "@norican/react";
import { useState } from "react";

import { LanguageSwitcher } from "@/components/layout";
import { useDocumentTitle } from "@/hooks";
import { useT } from "@/i18n";
import {
  KIOSK_CONTACT_MAX_LENGTH,
  KIOSK_NAME_MAX_LENGTH,
  type KioskContactProblem,
  type KioskNameProblem,
  validateKioskContact,
  validateKioskName,
} from "@/lib/kiosk";
import { setKioskPerson } from "@/lib/kiosk-person";

export interface KioskPersonStepProps {
  /** The shared device's own name, shown so people know which PC this is. */
  deviceName: string;
}

export function KioskPersonStep({ deviceName }: KioskPersonStepProps) {
  const { t } = useT();
  useDocumentTitle(t("kiosk.step.title"));
  const [name, setName] = useState("");
  const [contact, setContact] = useState("");
  const [nameProblem, setNameProblem] = useState<KioskNameProblem | null>(null);
  const [contactProblem, setContactProblem] = useState<KioskContactProblem | null>(null);

  const handleSubmit = () => {
    const nextNameProblem = validateKioskName(name);
    const nextContactProblem = validateKioskContact(contact);
    setNameProblem(nextNameProblem);
    setContactProblem(nextContactProblem);
    if (nextNameProblem !== null || nextContactProblem !== null) {
      return;
    }
    setKioskPerson({ name, contact });
  };

  return (
    <div className="app-surface flex min-h-screen flex-col items-center justify-center gap-4 px-4 py-10">
      <Card className="w-full max-w-md">
        <Card.Header className="flex-col items-start gap-2">
          <div className="flex items-center gap-2">
            <Icon icon={Monitor} size="md" tone="accent" />
            <Typography as="span" variant="caption" tone="muted">
              {deviceName}
            </Typography>
          </div>
          <Typography as="h1" variant="heading-3" className="text-balance">
            {t("kiosk.step.title")}
          </Typography>
          <Typography variant="body-sm" tone="muted" className="text-pretty">
            {t("kiosk.step.intro")}
          </Typography>
        </Card.Header>
        <Card.Body>
          <Form className="flex flex-col gap-5" onFormSubmit={handleSubmit}>
            <Field
              name="kiosk-name"
              label={t("kiosk.step.nameLabel")}
              error={nameProblem === null ? undefined : t(`kiosk.step.nameError.${nameProblem}`)}
              required
            >
              <Input
                name="kiosk-name"
                value={name}
                autoComplete="off"
                autoFocus
                maxLength={KIOSK_NAME_MAX_LENGTH}
                onChange={(event) => {
                  setName(event.target.value);
                  if (nameProblem !== null && validateKioskName(event.target.value) === null) {
                    setNameProblem(null);
                  }
                }}
              />
            </Field>
            <Field
              name="kiosk-contact"
              label={t("kiosk.step.contactLabel")}
              hint={t("kiosk.step.contactHint")}
              error={contactProblem === null ? undefined : t("kiosk.step.contactTooLong")}
            >
              <Input
                name="kiosk-contact"
                value={contact}
                autoComplete="off"
                maxLength={KIOSK_CONTACT_MAX_LENGTH}
                onChange={(event) => {
                  setContact(event.target.value);
                  if (
                    contactProblem !== null &&
                    validateKioskContact(event.target.value) === null
                  ) {
                    setContactProblem(null);
                  }
                }}
              />
            </Field>
            <Button type="submit" fullWidth>
              {t("kiosk.step.continue")}
            </Button>
          </Form>
        </Card.Body>
      </Card>
      {/* The shell (and its language picker) is behind this step, and the
          person at a shared PC is the least likely to read the device's
          default language. */}
      <LanguageSwitcher />
    </div>
  );
}
